import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

import type { Readiness } from '../types'

/**
 * La checklist de préparation.
 *
 * Elle est **calculée par le serveur**, jamais cochée à la main : « les
 * candidats sont approuvés » veut dire qu'ils le sont en base, pas que
 * quelqu'un a coché une case en y croyant. C'est ce qui en fait un garde-fou
 * plutôt qu'un pense-bête.
 */
export function ReadinessCard({ readiness }: { readiness: Readiness | undefined }) {
  const { t } = useTranslation()
  if (!readiness) return null

  const ratio = readiness.total > 0 ? readiness.completed / readiness.total : 0

  return (
    // Le collage en haut est porté par la colonne qui l'accueille : deux
    // éléments `sticky` empilés se chevauchent au défilement.
    <aside className="rounded-lg border border-line-subtle bg-bg-elevated p-6">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.readiness.title')}
      </h2>

      <p className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-display-sm tabular-nums text-champagne-300">
          {readiness.completed}
        </span>
        <span className="text-sm text-content-muted">/ {readiness.total}</span>
      </p>

      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-bg-overlay"
        role="progressbar"
        aria-valuenow={readiness.completed}
        aria-valuemin={0}
        aria-valuemax={readiness.total}
      >
        <div
          className="h-full rounded-full bg-[var(--gradient-ember)] transition-[width] duration-[var(--dur-slow)] ease-[var(--ease-out)]"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      <ol className="mt-5 space-y-2.5">
        {readiness.steps.map((step) => {
          // Toutes les étapes ne se valent pas : certaines ne sont que de la
          // préparation, d'autres interdisent la publication. Sans les
          // distinguer, on cherche lequel des points restants retient le
          // bouton « Publier ».
          const blocks = Boolean(step.blocking) && !step.done
          return (
          <li key={step.key} className="flex gap-3">
            <span
              aria-hidden
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]',
                step.done
                  ? 'bg-state-success/15 text-state-success'
                  : blocks
                    ? 'border border-state-warning/60 text-state-warning'
                    : 'border border-line text-content-muted',
              )}
            >
              {step.done ? '✓' : step.number}
            </span>
            <div className="min-w-0">
              <p
                className={cn(
                  'text-sm',
                  step.done ? 'text-content-secondary' : 'text-content-primary',
                )}
              >
                {/* Le libellé est du texte d'interface : il se traduit ici,
                    à partir de la clé stable de l'étape. Le serveur en
                    renvoie une version française, gardée en dernier recours
                    si une étape est ajoutée sans traduction. */}
                {t(`admin.readiness.step.${step.key}`, { defaultValue: step.label })}
                {blocks ? (
                  <span className="ml-2 inline-flex items-center rounded-full border border-state-warning/40 px-1.5 py-px align-middle text-[10px] uppercase tracking-wide text-state-warning">
                    {t('admin.editions.blockingBadge')}
                  </span>
                ) : null}
              </p>
              {step.detail ? (
                <p className="text-xs text-content-muted">{step.detail}</p>
              ) : null}
            </div>
          </li>
          )
        })}
      </ol>

      {readiness.blocking.length > 0 ? (
        <p className="mt-5 rounded-md border border-state-warning/30 bg-state-warning/5 px-3 py-2 text-xs text-content-secondary">
          {t('admin.readiness.blocking', { count: readiness.blocking.length })}
        </p>
      ) : readiness.can_publish ? (
        <p className="mt-5 text-xs text-state-success">{t('admin.readiness.ready')}</p>
      ) : null}
    </aside>
  )
}
